import axios from 'axios';
import { REACT_APP_API_URL, AUTH_USE_COOKIES } from '../../config/constants';
import type { IPlanFeatures } from '../auth/types';
import type { ICurrentUserMe } from '../users/types';
import { isConsultant } from '../../utils/isProfile';
import { normalizePlanFeatures } from '../../utils/normalizePlanFeatures';
import {
  getRefreshToken,
  getToken,
  hasStoredAccessToken,
  hasStoredRefreshToken,
  setTokens,
} from './tokenStorage';
import type { ICsrfResponse, IRefreshResponse } from './types';

const rawApi = axios.create({
  baseURL: REACT_APP_API_URL,
  withCredentials: AUTH_USE_COOKIES,
});

const SESSION_LOGGED_OUT_KEY = 'AUTH_SESSION_LOGGED_OUT';

let cachedCsrfToken: string | null = null;

export const markSessionLoggedOut = (): void => {
  sessionStorage.setItem(SESSION_LOGGED_OUT_KEY, '1');
};

export const clearSessionLoggedOut = (): void => {
  sessionStorage.removeItem(SESSION_LOGGED_OUT_KEY);
};

const isSessionLoggedOut = (): boolean => {
  return sessionStorage.getItem(SESSION_LOGGED_OUT_KEY) === '1';
};

export const clearCsrfCache = (): void => {
  cachedCsrfToken = null;
};

const buildAuthHeaders = (): Record<string, string> => {
  if (AUTH_USE_COOKIES) {
    return {};
  }
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const buildRefreshBody = (): Record<string, string> => {
  if (AUTH_USE_COOKIES) {
    return {};
  }
  const refreshToken = getRefreshToken();
  return refreshToken ? { refreshToken } : {};
};

const CONSULTANT_PLAN_FEATURES: IPlanFeatures = {
  indicators: false,
  quotes: true,
  priceComparison: false,
  chat: false,
  myStation: false,
  ai: false,
  stock: false,
};

export const fetchCurrentUser = async (options?: {
  skipAuthRefresh?: boolean;
}): Promise<ICurrentUserMe> => {
  const { default: apiClient } = await import('../axios');
  const response = await apiClient.get<ICurrentUserMe>('/users/me', {
    skipAuthRefresh: options?.skipAuthRefresh,
  });
  const user = response.data;
  const planFeatures = isConsultant(user)
    ? CONSULTANT_PLAN_FEATURES
    : normalizePlanFeatures(user.planFeatures);
  return {
    ...user,
    planFeatures,
  };
};

export const refreshSession = async (): Promise<IRefreshResponse> => {
  const response = await rawApi.post<IRefreshResponse>('/auth/refresh-token', buildRefreshBody(), {
    headers: buildAuthHeaders(),
  });

  const { accessToken, refreshToken: newRefreshToken } = response.data;
  if (!AUTH_USE_COOKIES && accessToken) {
    setTokens(accessToken, newRefreshToken);
  }

  return response.data;
};

export const logoutSession = async (): Promise<void> => {
  const { default: apiClient } = await import('../axios');
  try {
    await apiClient.post('/auth/logout', buildRefreshBody(), {
      skipAuthRefresh: true,
    });
  } catch (error) {
    console.error('Erro ao fazer logout no servidor:', error);
  } finally {
    clearCsrfCache();
  }
};

export const fetchCsrfToken = async (): Promise<string> => {
  const response = await rawApi.get<ICsrfResponse>('/auth/csrf', {
    headers: buildAuthHeaders(),
  });
  cachedCsrfToken = response.data.csrfToken;
  return cachedCsrfToken;
};

export const getCsrfToken = (): string | null => cachedCsrfToken;

export const readXsrfCookie = (): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Indica se vale tentar bootstrap de sessão (GET /users/me).
 * Com cookies HttpOnly, não há tokens no storage — usa withCredentials.
 */
export const hasSessionCredentials = (): boolean => {
  if (isSessionLoggedOut()) {
    return false;
  }
  if (hasStoredAccessToken() || hasStoredRefreshToken()) {
    return true;
  }
  return AUTH_USE_COOKIES;
};
